import type {
  CatalogImportPreviewDto,
  CatalogImportPreviewSessionDto,
  CatalogImportRowFieldOverride,
  ColumnMapping,
  ImportPreviewRowDto,
} from '../api/catalogImportTypes'
import { columnMappingFromPreview } from '../api/catalogImportTypes'

const storageKey = (organizationId: number): string => `catalog_import_draft_v1_${organizationId}`

export type CatalogImportDraft = {
  organizationId: number
  preview: CatalogImportPreviewDto
  columnMapping: ColumnMapping
  rowTypeOverrides: Record<number, number>
  rowFieldOverrides: Record<number, CatalogImportRowFieldOverride>
  excludedRowNumbers: number[]
  visibleRows: ImportPreviewRowDto[]
  savedAt: string
  offset?: number
  previewPage?: number
  matchingChangesFirst?: boolean
  matchingChangeRowNumbers?: number[]
  privateSupplierId?: number | null
}

type StoredDraft = {
  organizationId?: number
  preview: CatalogImportPreviewDto
  columnMapping: ColumnMapping
  rowTypeOverrides: Record<string, number>
  rowFieldOverrides: Record<string, CatalogImportRowFieldOverride>
  excludedRowNumbers?: number[]
  visibleRows: ImportPreviewRowDto[]
  savedAt: string
  offset?: number
  previewPage?: number
  matchingChangesFirst?: boolean
  matchingChangeRowNumbers?: number[]
  privateSupplierId?: number | null
}

import { isPreviewSessionExpired } from '../utils/catalogImportSession'

export const saveCatalogImportDraft = (
  organizationId: number,
  draft: Omit<CatalogImportDraft, 'savedAt' | 'organizationId'>,
): void => {
  const payload: StoredDraft = {
    organizationId,
    preview: draft.preview,
    columnMapping: draft.columnMapping,
    rowTypeOverrides: Object.fromEntries(
      Object.entries(draft.rowTypeOverrides).map(([key, value]) => [String(key), value]),
    ),
    rowFieldOverrides: Object.fromEntries(
      Object.entries(draft.rowFieldOverrides).map(([key, value]) => [String(key), value]),
    ),
    excludedRowNumbers: draft.excludedRowNumbers,
    visibleRows: draft.visibleRows,
    offset: draft.offset,
    previewPage: draft.previewPage,
    matchingChangesFirst: draft.matchingChangesFirst,
    matchingChangeRowNumbers: draft.matchingChangeRowNumbers,
    privateSupplierId: draft.privateSupplierId,
    savedAt: new Date().toISOString(),
  }
  localStorage.setItem(storageKey(organizationId), JSON.stringify(payload))
}

export const loadCatalogImportDraft = (organizationId: number): CatalogImportDraft | null => {
  const raw = localStorage.getItem(storageKey(organizationId))
  if (!raw) {
    return null
  }
  try {
    const parsed = JSON.parse(raw) as StoredDraft
    if (parsed.organizationId != null && parsed.organizationId !== organizationId) {
      return null
    }
    if (isPreviewSessionExpired(parsed.preview)) {
      clearCatalogImportDraft(organizationId)
      return null
    }
    const rowTypeOverrides: Record<number, number> = {}
    for (const [key, value] of Object.entries(parsed.rowTypeOverrides ?? {})) {
      rowTypeOverrides[Number(key)] = value
    }
    const rowFieldOverrides: Record<number, CatalogImportRowFieldOverride> = {}
    for (const [key, value] of Object.entries(parsed.rowFieldOverrides ?? {})) {
      rowFieldOverrides[Number(key)] = value
    }
    return {
      organizationId,
      preview: parsed.preview,
      columnMapping: parsed.columnMapping,
      rowTypeOverrides,
      rowFieldOverrides,
      excludedRowNumbers: parsed.excludedRowNumbers ?? [],
      visibleRows:
        parsed.visibleRows.length > 0 ? parsed.visibleRows : parsed.preview.sampleRows,
      savedAt: parsed.savedAt,
      offset: parsed.offset ?? 0,
      previewPage: parsed.previewPage ?? Math.floor(Math.max(parsed.offset ?? 0, 0) / 25),
      matchingChangesFirst: parsed.matchingChangesFirst ?? false,
      matchingChangeRowNumbers: parsed.matchingChangeRowNumbers ?? [],
      privateSupplierId: parsed.privateSupplierId ?? null,
    }
  } catch {
    clearCatalogImportDraft(organizationId)
    return null
  }
}

export const clearCatalogImportDraft = (organizationId: number): void => {
  localStorage.removeItem(storageKey(organizationId))
}

export const formatDraftExpiry = (expiresAt: string | null): string => {
  if (!expiresAt) {
    return 'окончания серверной сессии'
  }
  const date = new Date(expiresAt)
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const draftFromServerSession = (
  session: CatalogImportPreviewSessionDto,
  pageSize = 25,
): CatalogImportDraft => {
  const mapping = columnMappingFromPreview(session.preview)
  for (const [field, index] of Object.entries(session.review.columnMapping ?? {})) {
    if (typeof index === 'number') {
      mapping[field] = index
    }
  }
  const offset = Math.max(session.review.offset ?? 0, 0)
  return {
    organizationId: session.preview.organizationId,
    preview: session.preview,
    columnMapping: mapping,
    rowTypeOverrides: session.review.rowProductTypeOverrides ?? {},
    rowFieldOverrides: session.review.rowFieldOverrides ?? {},
    excludedRowNumbers: session.review.excludedRowNumbers ?? [],
    visibleRows: session.preview.sampleRows ?? [],
    savedAt: session.review.savedAt ?? new Date().toISOString(),
    offset,
    previewPage: Math.floor(offset / pageSize),
    matchingChangesFirst: session.review.matchingChangesFirst ?? false,
    matchingChangeRowNumbers: session.review.matchingChangeRowNumbers ?? [],
    privateSupplierId: session.review.privateSupplierId ?? null,
  }
}
